import { useCallback, useEffect } from 'react'
import { useWeb3React } from 'lib/wagmi-compat/useWeb3React'
import { isSupportedChainId } from 'connectors/utils'
import { useTranslation } from 'react-i18next'
import { ConnectorName, ConnectorNameEnum, ERROR_CODES, ETH_CHAIN_ID_NUMBER } from '@mprolab/sdk'
import { accountActiveStore } from 'mobxStores/AccountActive'
import { getChainIdNumber } from 'utils/getChainIdNumber'
import { reportClientError, setReportingWallet } from 'utils/reportClientError'
import { metamaskMobileStore } from 'mobxStores/MetamaskMobile'
import useAuth from './useAuth'
import useToast from './useToast'
import { useSetupNetwork } from './useSetupNetwork'

export const useAutoNetworkSwitching = () => {
  const web3 = useWeb3React()
  const { logout, login } = useAuth()
  const { toastError } = useToast()
  const { t } = useTranslation()
  const { setupNetwork } = useSetupNetwork()

  const web3Activate = useCallback(
    async (chainId: number | string) => {
      const chainIdNumber = getChainIdNumber(chainId)
      const connectorId = window.localStorage.getItem('connectorId')
      if (connectorId && connectorId !== ConnectorName.Injected) return
      const isSupported = isSupportedChainId(chainIdNumber as number)

      if (connectorId === ConnectorName.Injected) {
        const showNetworkErrorToast = () => {
          toastError(
            t('walletConnect.networkError'),
            '',
            'Switch network to login',
            async () => {
              const isSwitched = await setupNetwork({ chainId: ETH_CHAIN_ID_NUMBER, error: false })
              if (isSwitched)
                await login({
                  connectorId: ConnectorName.Injected,
                  title: window?.ethereum?.isMetaMask ? ConnectorNameEnum.METAMASK : ConnectorNameEnum.TRUSTWALLET,
                  customChainId: ETH_CHAIN_ID_NUMBER
                })
              else logout({ connector: web3.connector, autoDisconnect: true })
            },
            6000
          )
        }

        if (!isSupported && accountActiveStore.isActive) {
          await logout({ connector: web3.connector, autoDisconnect: true, networkChanged: true })
          showNetworkErrorToast()
        } else if (!accountActiveStore.isActive && !isSupported) {
          showNetworkErrorToast()
        } else if (accountActiveStore.isActive && isSupported) {
          await web3.connector?.activate?.(chainIdNumber)
        }
      }
    },
    [web3.connector, web3.account]
  )

  useEffect(() => {
    const connectorId = typeof window !== 'undefined' ? window.localStorage.getItem('connectorId') : null
    if (connectorId !== ConnectorName.WalletConnect || !web3.connector) {
      return undefined
    }

    const wcConnector = web3.connector
    let cancelled = false
    let cleanup: (() => void) | undefined

    const activate = async (chainId: string | number) => {
      let chainIdNumber = chainId
      if (typeof chainId === 'string') {
        chainIdNumber = parseInt(chainId, 16)
      }

      if (!isSupportedChainId(chainIdNumber as number)) {
        return
      }
      // @ts-ignore
      if (web3?.connector?.provider?.session?.peer?.metadata?.redirect?.native === 'metamask://') {
        metamaskMobileStore.setLoading(true)
      }
      const cid = window.localStorage.getItem('connectorId')
      if (cid && cid !== ConnectorName.WalletConnect) return

      const chainToConnect = isSupportedChainId(chainIdNumber as number) ? chainIdNumber : ETH_CHAIN_ID_NUMBER

      /*
       * This runs from the wallet's own `chainChanged` event, so the wallet is
       * ALREADY on the new chain — we are only telling the connector to follow.
       * WalletConnect answers that with "does not support programmatic chain
       * switching" and, because an event handler's promise has nobody to catch
       * it, twenty-five of those reached observability from nine wallets as
       * unhandled rejections between 4 and 6 September. Nothing was broken for
       * those people and there was nothing for them to do, so this is caught
       * here: the refusal is expected and silent, anything else is recorded.
       */
      try {
        await wcConnector.activate?.(chainToConnect as number)
      } catch (error) {
        const message = String((error as { message?: unknown })?.message ?? error ?? '')
        if (message.includes(ERROR_CODES.WALLETCONNECT_NO_CHAIN_SWITCH)) return
        reportClientError({
          message: `wallet.followChain: ${message.slice(0, 200)}`,
          kind: 'wallet',
          handled: true
        })
      }
    }

    const deactivate = () => {
      logout({ connector: wcConnector })
    }

    // A connection restored from localStorage gives back the SERIALISED connector
    // — {id, name, type, uid} — not a live instance, so getProvider simply is not
    // there. Calling it unguarded threw inside this effect and React unmounted the
    // whole application: a blank page on every route for every returning
    // WalletConnect user. The line above already guards `activate?.()`; this one
    // did not.
    const providerPromise = wcConnector?.getProvider?.()
    if (!providerPromise || typeof providerPromise.then !== 'function') {
      return () => {
        cancelled = true
      }
    }

    void providerPromise
      .then((prov: any) => {
        if (cancelled || !prov?.events) return
        prov.events.on('chainChanged', activate)
        prov.events.once('disconnect', deactivate)
        cleanup = () => {
          prov.events?.removeListener?.('chainChanged', activate)
          prov.events?.removeListener?.('disconnect', deactivate)
        }
      })
      // A wallet that does not answer is a wallet that does not answer. It must
      // not become an unhandled rejection, and it must not cost the user the page.
      .catch(() => undefined)

    return () => {
      cancelled = true
      cleanup?.()
    }
  }, [web3.connector, logout])

  // Mounted app-wide in AppLayout, so this is where the connected wallet is
  // always known — and every later report can name who hit the failure.
  useEffect(() => {
    setReportingWallet(web3.account)
  }, [web3.account])

  useEffect(() => {
    const ethereum = (window.ethereum || window.trustwallet) as any
    if (!ethereum?.on) return undefined

    const listener = async (chainId: string) => web3Activate(chainId)
    ethereum.on('chainChanged', listener)

    /**
     * `accountsChanged` for the injected wallet, which nothing listened for.
     *
     * WalletConnect has always forwarded it (lib/walletConnectWithUri.ts) and
     * the injected path never did — so switching accounts in MetaMask left the
     * app holding the previous one until something else happened to refresh
     * it. Reported symptom: connecting a second wallet failed once and worked
     * on a retry, which is what a stale account looks like from outside.
     *
     * It only acts on a real change. wagmi's own connector reacts to this
     * event too, and re-activating on every emission would fight it.
     */
    const accountsListener = (accounts: string[]) => {
      const next = accounts?.[0]?.toLowerCase()
      const current = web3.account?.toLowerCase()
      if (!next || next === current) return
      reportClientError({
        kind: 'wallet',
        message: `injected accountsChanged: app held ${current ?? 'none'}, wallet is ${next}`,
        wallet: next,
        // Not a fault — recorded so a switch that goes wrong is visible at all.
        handled: true
      })
      void web3Activate(web3.provider?._network?.chainId ?? ETH_CHAIN_ID_NUMBER)
    }
    ethereum.on('accountsChanged', accountsListener)

    return () => {
      ethereum.removeListener('chainChanged', listener)
      ethereum.removeListener('accountsChanged', accountsListener)
    }
  }, [web3Activate, web3.account, web3.provider?._network?.chainId])
}
